import {
  WSElement,
  WSFlexBox,
  WSIcon,
  WSList,
  WSLoader,
  WSSectionToolbar,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import React from "react";

import { useQueryPayeeRowsSummary } from "../../../../query/search/payee/queries/useQueryPayeeRowsSummary";
import { useVersionQuickReferenceModal } from "../VersionQuickReferenceModal";

type Props = {
  requirementDefinition: IRequirementDefinitionResponse;
  viewingVersion: number;
};

type TableItem = {
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
};

const ContractorsCell: React.FC<{
  isLatestVersion: boolean;
  requirementDefinitionId: string;
  version: number;
}> = ({ isLatestVersion, requirementDefinitionId, version }) => {
  const queryTotal = useQueryPayeeRowsSummary({
    filter: {
      "requirements.searchableRequirementDefinitionVersion": {
        "=": `${requirementDefinitionId}_${version}`
      }
    }
  });

  if (queryTotal.isLoading) {
    return <WSLoader.Spinner size="XS" />;
  }

  const total = queryTotal.data?.listSize ?? 0;
  const label = isLatestVersion ? `${total} + new` : String(total);

  return <WSTableCell text={label} />;
};

const getTableColumns = (
  viewingVersion: number
): WSTableColumn<TableItem>[] => [
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Version" }
    },
    renderFunction: ({ data }) => (
      <WSList.Horizontal fullWidth gap="M" justify="space-between">
        <WSText.ParagraphSm>v{data.version}</WSText.ParagraphSm>
        {data.version === viewingVersion ? (
          <WSFlexBox.CenterY gap="XS">
            <WSIcon color="blue500" name="view" size="S" />
            <WSText.ParagraphXs color="blue500" weight="medium">
              Viewing
            </WSText.ParagraphXs>
          </WSFlexBox.CenterY>
        ) : null}
      </WSList.Horizontal>
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      header: { text: "Contractors" }
    },
    renderFunction: ({ data }) => (
      <ContractorsCell
        isLatestVersion={data.isLatestVersion}
        requirementDefinitionId={data.requirementDefinitionId}
        version={data.version}
      />
    )
  }
];

export const VersionsWidget: React.FC<Props> = ({
  requirementDefinition,
  viewingVersion
}) => {
  const versionQuickReferenceModal = useVersionQuickReferenceModal();
  const { versionHistory } = requirementDefinition;

  if (!versionHistory?.length) {
    return null;
  }

  const latestVersion = requirementDefinition.version;
  const { requirementDefinitionId } = requirementDefinition;

  const tableData: WSTableItem<TableItem>[] = [
    {
      data: {
        isLatestVersion: true,
        requirementDefinitionId,
        version: latestVersion
      },
      id: `${latestVersion}`
    },
    ...versionHistory.map(v => ({
      data: {
        isLatestVersion: false,
        requirementDefinitionId,
        version: v.version
      },
      id: `${v.version}`
    }))
  ].sort((a, b) => b.data.version - a.data.version);

  const columns = getTableColumns(viewingVersion);

  return (
    <WSElement>
      <WSSectionToolbar
        buttons={[
          {
            hidden: true,
            label: "View details",
            onClick: () => {
              versionQuickReferenceModal.open({
                requirementDefinitionId:
                  requirementDefinition.requirementDefinitionId
              });
            }
          }
        ]}
        title="Active versions"
      />
      <WSTable<TableItem>
        columns={columns}
        mt="M"
        showVerticalDivider
        tableData={tableData}
      />
    </WSElement>
  );
};
